import clamp from 'lodash/clamp';
import sortBy from 'lodash/sortBy';
import {
  TARGET_PATH_FIRST_YEAR,
  TARGET_PATH_LAST_YEAR,
  TARGET_PATH_YEARS_RANGE,
  ValidYear,
} from './TargetPathConstants';

/**
 * The first year covered by a target path array of the given length, assuming (per
 * {@link yearToPathIndex}) that the last entry always corresponds to TARGET_PATH_LAST_YEAR.
 * A path shorter than the full TARGET_PATH_YEARS_RANGE starts later than TARGET_PATH_FIRST_YEAR —
 * e.g. for a building constructed after TARGET_PATH_FIRST_YEAR, whose path only covers years from
 * construction onward.
 * TODO remove this: RED-2968
 */
export function getPathStartYear(pathLength: number): number {
  return TARGET_PATH_YEARS_RANGE.length - pathLength + TARGET_PATH_FIRST_YEAR;
}

/**
 * Converts a given year to its corresponding index position in a target path array.
 * This function assumes the last value in any given path corresponds to TARGET_PATH_LAST_YEAR.
 * The provided year is clamped between TARGET_PATH_FIRST_YEAR and TARGET_PATH_LAST_YEAR.
 *
 * @param year - The year to convert to a path index
 * @param pathLength - The total length of the target path array
 * @returns The index position in the target path array corresponding to the given year
 * @throws {Error} When the provided year is not within the valid range of the target path
 */
export const yearToPathIndex = (year: number, pathLength: number): number => {
  if (pathLength === 0) {
    console.error('yearToPathIndex: `pathLength` should not be 0');
    return Number.MAX_SAFE_INTEGER;
  }

  const pathStartYear = getPathStartYear(pathLength);
  const clampedYear = clamp(year, TARGET_PATH_FIRST_YEAR, TARGET_PATH_LAST_YEAR);

  if (clampedYear >= pathStartYear) return clampedYear - pathStartYear;

  throw new Error(`Year ${clampedYear} is not within the range of the target path`);
};

export function pathValueAtYear<T>(path: T[], year: number): T {
  return path[yearToPathIndex(year, path.length)];
}

export function pathValuesFromYearToYear<T>(path: T[], startYear: number, endYear: number): T[] {
  if (path.length === 0) return [];

  const pathLength = path.length;
  const emptyPath = new Array(pathLength).fill(null);

  return [
    ...emptyPath.slice(0, yearToPathIndex(startYear, pathLength)),
    ...path.slice(yearToPathIndex(startYear, pathLength), yearToPathIndex(endYear, pathLength) + 1),
    ...emptyPath.slice(yearToPathIndex(endYear, pathLength) + 1),
  ];
}

export function pathValuesFromYear<T>(path: T[], year: number): T[] {
  if (path.length === 0) return [];

  return path.slice(yearToPathIndex(year, path.length));
}

export function pathValuesUntilYear<T>(path: T[], year: number): T[] {
  if (path.length === 0) return [];

  return path.slice(0, yearToPathIndex(year, path.length));
}

export function pathValuesWithYearFromYear<T>(path: T[], year: number = TARGET_PATH_FIRST_YEAR): [number, T][] {
  if (!path || !year) return [];

  const pathFromYear = pathValuesFromYear(path, year);
  return pathValuesWithYear(pathFromYear, year);
}

export function pathValuesWithYear<T>(path: T[] | undefined, year: number = TARGET_PATH_FIRST_YEAR): [number, T][] {
  if (path === undefined || path.length === 0) return [];

  return path.map((value, i) => [year + i, value]);
}

export function co2EmissionsFromYear(emissions: number[], year: number): number[] {
  return emissions.slice(yearToPathIndex(year, emissions.length));
}

export function pathToYearValueMap(
  path: number[],
  constructionYear: number = TARGET_PATH_FIRST_YEAR,
): { year: number; value: number }[] {
  const pathStartYear = determineFirstPathYear(constructionYear);
  return path.map((value, index) => ({ year: pathStartYear + index, value }));
}

/*
 *  Assumes that a path shorter than TARGET_PATH_YEARS_RANGE, has values until TARGET_PATH_LAST_YEAR (2050).
 *  Meaning, a path with length === 1 only has a value for year 2050.
 *  Fills missing years with 0.
 * */
export function toStandardPathLength(path: number[], fillValue = 0) {
  if (path.length === TARGET_PATH_YEARS_RANGE.length) return path;
  if (path.length === 0) return Array.from({ length: TARGET_PATH_YEARS_RANGE.length }, () => fillValue);

  const pathStartYear = getPathStartYear(path.length);
  return TARGET_PATH_YEARS_RANGE.map((it) => (it < pathStartYear ? fillValue : path[yearToPathIndex(it, path.length)]));
}

/**
 * Given an active building model ("status quo") and energy paths of each action,
 * this function merges the energy paths of the actions into a single path and retrieves the value at a given year.
 */
export function getValueFromMergedPath<T>(
  basePath: T[],
  actionData: { data: T[]; implementation_year: number; order: number }[],
  year: number,
): T {
  let currentData = basePath;

  const lastActionBeforeYear = sortBy(
    actionData.filter((action) => action.implementation_year <= year),
    (action) => action.order,
  ).pop();

  if (lastActionBeforeYear) {
    currentData = lastActionBeforeYear.data;
  }

  return pathValueAtYear(currentData, year);
}

/**
 * Merges a base energy path with energy paths associated with a series of sortedActions, replacing data
 * after the `implementation_to` year for each action with the respective energy path values.
 *
 * @param {T[]} basePath - The initial energy path to be merged with action energy paths.
 * @param {{ implementation_to: Date; energy_path: T[] }[]} sortedActions - An sorted array of action objects, where each object contains:
 *    - `implementation_to`: A `Date` object representing the implementation end year for the action.
 *    - `energy_path`: An array of energy path values specific to the action.
 * @return {T[]} A new array representing the merged energy path.
 */
export function mergeBuildingBaseAndActionEnergyPaths<T>(
  basePath: T[],
  sortedActions: { implementation_to: Date; energy_path: T[] }[],
): T[] {
  const mergedPath: T[] = [...basePath];
  for (const action of sortedActions) {
    if (action.energy_path.length !== basePath.length) {
      throw new Error(
        `The length of the energy path for action in year ${action.implementation_to.getFullYear()} does not match the length of the base path.`,
      );
    }

    const actionEnergyPath = action.energy_path;
    const actionImplementToYear = action.implementation_to.getFullYear();
    const actionEnergyValuesFromImplementationToYear = pathValuesFromYear(actionEnergyPath, actionImplementToYear);
    mergedPath.splice(
      yearToPathIndex(actionImplementToYear, mergedPath.length),
      actionEnergyValuesFromImplementationToYear.length,
      ...actionEnergyValuesFromImplementationToYear,
    );
  }
  return mergedPath;
}

/**
 * Given an active building model ("status quo") and energy paths of each action,
 * this function retrieves the value at a given year from the last action that was implemented - independently of the
 * year.
 */
export function getValueFromLastAction<T>(
  basePath: T[],
  actionData: { data: T[]; implementation_year: number; order: number }[],
  year: number,
): T {
  let currentData = basePath;

  const lastAction = sortBy(actionData, (action) => action.order).pop();

  if (lastAction) {
    currentData = lastAction.data;
  }

  return pathValueAtYear(currentData, year);
}

/**
 * Determine the start year for the graph.
 * it should start from building construction date.ut if construction date is less than 2020 (e.g 1962) the it should
 * start from 2020 (from which we have data available)
 */
export const determineFirstPathYear = (constructionYear: number): ValidYear => {
  if (constructionYear < TARGET_PATH_FIRST_YEAR) {
    return TARGET_PATH_FIRST_YEAR;
  }

  if (constructionYear <= TARGET_PATH_LAST_YEAR) {
    return constructionYear as ValidYear;
  }

  throw new Error(
    `Construction year ${constructionYear} is not within the valid range (${TARGET_PATH_FIRST_YEAR} - ${TARGET_PATH_LAST_YEAR})`,
  );
};
